import { memo, useCallback } from 'react'
import { TokenMinimalData } from '../../types/token'
import { useTranslation } from 'next-i18next'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import AddressDisplay from '@components/shared/AddressDisplay'
import ImageWithFallback from '@components/image/ImageWithFallback'
import Link from 'next/link'
import { abbreviateAddress } from 'utils/formatting'
import Tooltip from '@components/shared/Tooltip'
import { LinkButton } from '@components/shared/Button'
import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/20/solid'
import { ExternalLink } from '@components/shared/ExternalLink'
import OpenExplorerLinkIcon from '@components/shared/OpenExplorerLinkIcon'
import { usePreferredExplorerUrl } from 'hooks/usePreferredExplorerUrl'

export const TokenAddress = memo(function TokenAddress({
  token,
  className = '',
  addressAsLink = false,
  showTokenImage,
}: {
  token: TokenMinimalData
  className?: string
  addressAsLink?: boolean
  showTokenImage?: boolean
}) {
  const { t } = useTranslation('components')
  const { isCopied, handleCopyAddress } = useCopyToClipboard()
  const makeExplorerUrlForMintAddress = usePreferredExplorerUrl('address')

  const handleCopy = useCallback(() => {
    handleCopyAddress(token.mint)
  }, [token.mint, handleCopyAddress])

  return (
    <AddressDisplay className={className}>
      {showTokenImage && (
        <ImageWithFallback
          src={token.image_url}
          height={12}
          width={12}
          alt={`${token.symbol} token logo`}
          className="mr-1"
        />
      )}
      {addressAsLink ? (
        <Link href={`/token/${token.mint}`} className="mr-1.5 text-xs">
          {abbreviateAddress(token.mint)}
        </Link>
      ) : (
        <span className="mr-1.5 text-xs">{abbreviateAddress(token.mint)}</span>
      )}
      <Tooltip
        content={t('shared.address-with-link-and-copy.copy-address', {
          symbol: token.symbol,
        })}
      >
        <LinkButton className="mr-2 text-th-fgd-3" onClick={handleCopy}>
          {isCopied ? (
            <CheckCircleIcon className="size-4 text-th-up" />
          ) : (
            <DocumentDuplicateIcon className="size-4" />
          )}
        </LinkButton>
      </Tooltip>
      <ExternalLink
        className="text-th-fgd-3"
        href={makeExplorerUrlForMintAddress(token.mint)}
      >
        <OpenExplorerLinkIcon className="size-4" />
      </ExternalLink>
    </AddressDisplay>
  )
})
